import { GodiverseListWithFilterProps } from '@src/components/godiverses/list/GodiverseListWithFilter'
import { PlanetListWithFilterProps } from '@src/components/planet/list/PlanetListWithFilter'
import { StarListWithFilterProps } from '@src/components/stars/list/StarListWithFilter'
import { GAAssetType } from '@src/constants/googleAnalytics'
import useTracking from '@src/hooks/common/analytics/useTracking'
import { ComponentType, useCallback } from 'react'

type ComponentProps<T> = T extends GAAssetType.Godiverse
  ? GodiverseListWithFilterProps
  : T extends GAAssetType.Planet
  ? PlanetListWithFilterProps
  : T extends GAAssetType.Star
  ? StarListWithFilterProps
  : never

type GAAssetTypeUnion =
  | GAAssetType.Apostle
  | GAAssetType.Godiverse
  | GAAssetType.Planet
  | GAAssetType.Star

const withListTracking =
  <T extends GAAssetTypeUnion>(type: T) =>
  (Component: ComponentType<ComponentProps<T>>) => {
    // 'any' type to escape type error
    const WrappedComponent = (props: ComponentProps<T> | any) => {
      const { gtmEmitViewItemList, gtmEmitSelectItem, gtmEmitFilter } =
        useTracking()

      const { onFetchSuccess, onItemClick } = props

      const handleOnItemClick: NonNullable<ComponentProps<T>['onItemClick']> =
        useCallback(
          (item: any) => {
            if (onItemClick) {
              onItemClick(item)
            }

            // GA-fbsDMxUmftyD86u
            // GA-l19IautYNlOrhZA
            // GA-ewayHKn4kvvlS7Q
            // GA-IB8kdzyFa8p3ina
            gtmEmitSelectItem({
              type,
              item
            })
          },
          [gtmEmitSelectItem, onItemClick]
        )

      const handleOnFetchSuccess = useCallback<
        NonNullable<ComponentProps<T>['onFetchSuccess']>
      >(
        (items: any[], filterHasChange: boolean, filterInput?: any) => {
          if (onFetchSuccess) {
            onFetchSuccess(items, filterHasChange, filterInput)
          }

          // GA-BOsYe9IsdtdlZGQ
          // GA-1AWqyt2wvn23fBC
          // GA-QoDbU6a9TYwBIVz
          // GA-Ks6di3aFmWJAYsI
          gtmEmitViewItemList({
            type,
            items
          })
        },
        [gtmEmitViewItemList, onFetchSuccess]
      )

      const handleOnAfterFilterChange = useCallback(
        (
          type: GAAssetType,
          originalFilter: Record<string, any>,
          newFilter: Record<string, any>
        ) => {
          // GA-nAo6l6x6PTawYNq
          // GA-xRc08cJMLU69Ng-C
          // GA-Cg22ETl_EPY-ikze
          // GA-vAOzjsB7KKVDHqpI
          for (const key in newFilter) {
            if (typeof newFilter[key] !== 'object') {
              if (
                newFilter[key] !== originalFilter[key] &&
                newFilter[key] != null
              ) {
                const value = newFilter[key].toString()

                gtmEmitFilter({
                  label: key,
                  value,
                  type
                })
              }
            } else if (Array.isArray(newFilter[key])) {
              for (const item of newFilter[key]) {
                if (
                  !originalFilter[key] ||
                  !originalFilter[key].includes(item)
                ) {
                  const value = item.toString()

                  gtmEmitFilter({
                    label: key,
                    value,
                    type
                  })
                }
              }
            }
          }
        },
        [gtmEmitFilter]
      )

      return (
        <Component
          {...props}
          onFetchSuccess={handleOnFetchSuccess}
          onItemClick={handleOnItemClick}
          onAfterFilterChange={handleOnAfterFilterChange}
        />
      )
    }

    WrappedComponent.displayName = `withTracking(${
      Component.displayName || Component.name || 'Component'
    })`

    return WrappedComponent
  }

export default withListTracking
